import http from './http';

// Service de la « suppression intégrale d'un site » (hosting-migration B2_09).
// Contrôleur : api/src/controllers/website_purge_controller.js — 4 routes suadmin (ensureAdmin) :
//   GET  /suadmin/website/:id/purge/inventory  → la liste chiffrée (n'écrit rien)
//   POST /suadmin/website/:id/purge            → lance OU reprend : 202 { job_id, status, resumed }
//   GET  /suadmin/website/:id/purge/status     → avancement (répond encore quand la fiche a disparu)
//   POST /suadmin/website/:id/purge/stop       → arrêt après la tranche en cours : 202
//
// ⛔ À NE PAS CONFONDRE avec deletionService.confirmDelete (« Confirmer suppression communauté ») :
// celui-là ANONYMISE et garde la donnée (usage réquisitions) ; celui-ci EFFACE un site qui pollue
// la base. Deux usages, deux boutons (décision Mathias 2026-09-20).
//
// http.js préfixe déjà REACT_APP_API_URL (qui CONTIENT `/api`) → chemins SANS `/api`.

const base = (websiteId) => `/suadmin/website/${encodeURIComponent(websiteId)}/purge`;

// ⛔ GARDE « HTTP 200 + { gcCode: 410 } » = session expirée, PAS une donnée. `suadmin` pose
// `ensureAuthenticated` AVANT `ensureAdmin`, et ce middleware répond 200 + gcCode sur un JWT
// expiré ; http.js ne lève que sur `!res.ok`. Sans cette garde, un clic « Supprimer » sur une
// session morte afficherait « suppression lancée » alors que RIEN n'est parti — sur une action
// irréversible, c'est le pire faux positif possible. Pattern : hostingDetectedService.rejectGcCode.
export const SESSION_EXPIRED = 'session_expired';

function rejectGcCode(result) {
  if (result === undefined || result === null || typeof result !== 'object') {
    const error = new Error('réponse inattendue du serveur (corps non JSON).');
    error.code = 'unexpected_response';
    throw error;
  }
  const hasErr = result.err && result.err !== 'undefined';
  if (result.gcCode || hasErr) {
    const error = new Error('session expirée ou accès refusé. Recharge la page pour te reconnecter.');
    error.code = SESSION_EXPIRED;
    error.body = result;
    throw error;
  }
  return result;
}

export const getInventory = (websiteId) => http.get(`${base(websiteId)}/inventory`).then(rejectGcCode);

// Lance, ou REPREND si un job arrêté / interrompu / sans signe de vie existe pour ce site.
// `confirm: true` est exigé par l'API ; le débit est optionnel (défaut serveur).
export const start = (websiteId, { maxDocsPerSecond } = {}) => {
  const body = { confirm: true };
  if (maxDocsPerSecond !== undefined && maxDocsPerSecond !== null && maxDocsPerSecond !== '') {
    body.max_docs_per_second = Number(maxDocsPerSecond);
  }
  return http.post(base(websiteId), body).then(rejectGcCode);
};

export const getStatus = (websiteId) => http.get(`${base(websiteId)}/status`).then(rejectGcCode);

export const stop = (websiteId) => http.post(`${base(websiteId)}/stop`, {}).then(rejectGcCode);

// http.js lève `Error('HTTP <n>: <corps>')` sur tout `!res.ok`, AVANT que rejectGcCode voie le
// corps : le statut et le code métier se lisent dans le message (err.status n'existe pas ici).
export const parseApiError = (err) => {
  const out = { status: null, code: null, msg: null, blockers: null, activeJob: null };
  if (!err || typeof err.message !== 'string') return out;
  if (err.code === SESSION_EXPIRED) return { ...out, code: SESSION_EXPIRED, msg: err.message };
  const m = /^HTTP (\d{3}): ?([\s\S]*)$/.exec(err.message);
  if (!m) return { ...out, msg: err.message };
  out.status = Number(m[1]);
  try {
    const body = JSON.parse(m[2]);
    out.code = body.code || null;
    out.msg = body.msg || null;
    out.blockers = Array.isArray(body.blockers) ? body.blockers : null;
    // 409 `purge_already_running` : l'API dit QUEL site tient la place (une purge à la fois).
    out.activeJob = body.active_job && typeof body.active_job === 'object' ? body.active_job : null;
  } catch (e) {
    out.msg = m[2] || null;
  }
  return out;
};

// 404 sur /status = « aucune suppression pour ce site » : un état NORMAL, pas une erreur.
export const isNoPurge = (err) => parseApiError(err).status === 404;

const websitePurgeService = { getInventory, start, getStatus, stop, parseApiError, isNoPurge, SESSION_EXPIRED };
export default websitePurgeService;
